import type { ReactNode } from "react";
import Link from "next/link";
import { Plate } from "./plate";

export interface Crumb {
  label: string;
  href?: string;
}

interface BreadcrumbProps {
  crumbs: Crumb[];
  label?: string;
  /** Keep one line and truncate the page's own crumb (a post title that repeats the h1). */
  truncateLast?: boolean;
  /** Below `sm` the page's own crumb is clipped (kept for screen readers): the h1 right under it says the same. */
  collapseLastBelowSm?: boolean;
  className?: string;
}

/** The one breadcrumb: real page names, sentence case, body-sm. */
export function Breadcrumb({ crumbs, label = "Breadcrumb", truncateLast = false, collapseLastBelowSm = false, className }: BreadcrumbProps) {
  return (
    <nav aria-label={label} className={className}>
      <ol className={`tls-breadcrumb m-0 flex list-none items-center gap-x-2 gap-y-1 p-0 ${truncateLast ? "flex-nowrap" : "flex-wrap"}`}>
        {crumbs.map((crumb, i) => {
          const last = i === crumbs.length - 1;
          return (
            // Only the page's own crumb may wrap; at 200% on a phone the
            // others otherwise break mid-word ("Ho me"). A link is a 44/48px
            // touch target on a coarse pointer, so it centres its own text
            // to stay on the separators' and the current crumb's line.
            <li
              key={`${crumb.label}-${i}`}
              className={`inline-flex max-w-full items-center gap-2 ${last && collapseLastBelowSm ? "sr-only sm:not-sr-only" : ""} ${last ? `min-w-0 ${truncateLast ? "" : "[overflow-wrap:anywhere]"}` : "shrink-0 whitespace-nowrap"}`}
            >
              {i > 0 && (
                <span aria-hidden="true" className="text-[var(--tls-fg-faint)]">
                  /
                </span>
              )}
              {crumb.href && !last ? (
                <Link prefetch={false} href={crumb.href} className="tlsr-focus tls-touch-pad inline-flex items-center text-[var(--tls-fg-subtle)] no-underline transition-colors hover:text-[var(--tls-fg-muted)]">
                  {crumb.label}
                </Link>
              ) : (
                <span className={`text-[var(--tls-fg-muted)] ${last && truncateLast ? "min-w-0 truncate" : ""}`} aria-current={last ? "page" : undefined}>
                  {crumb.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

interface PageTopProps {
  crumbs: Crumb[];
  /** The breadcrumb landmark's name, for localised pages. */
  breadcrumbLabel?: string;
  /** See Breadcrumb's truncateLast. */
  truncateCrumb?: boolean;
  /**
   * The h1 text. On a plate page it is the descriptor line under the plate,
   * inside the same h1, so the keyword phrase a crawler reads keeps its
   * substance (owner ruling 2026-10-02).
   */
  title: string;
  /** A short subject ("FAQ", "Comedy"). Omit for a page without a plate. */
  plate?: string;
  /**
   * The h1 size without a plate: `page` is .tls-h1 (legal, about); `tool` is
   * the 56/800 sentence-case heading of the tool pages.
   */
  heading?: "page" | "tool";
  lede?: ReactNode;
  /** Show the lede from `sm` only, where a phone fold needs the list instead. */
  ledeFromSm?: boolean;
  /** Server-pass plate column at 390 and 1440; see PlateHeading. */
  usable?: number;
  usableDesktop?: number;
  /** A column beside the title band at lg; it sets its own width. */
  aside?: ReactNode;
  /** Align the title band and the aside on their bottoms (covers) or tops (tool panels). */
  asideAlign?: "start" | "end";
  /** A meta line or action row under the lede, in the title column beside any aside. */
  meta?: ReactNode;
  /** Full-width extras under the title band and aside (hub preview, install rail). */
  children?: ReactNode;
  /** Caps the column at a prose measure, on the shared left edge. */
  measure?: "prose" | "mid" | "wide";
  /** The hairline under the top. Off when the first block below draws its own. */
  rule?: boolean;
  /** 16px under the top instead of 48, for a page whose tool starts right below (the library's search). */
  tight?: boolean;
  className?: string;
}

const MEASURE = { prose: "tls-measure-prose", mid: "tls-measure-mid", wide: "tls-measure-wide" } as const;
/** The plate's column at 1440 for each measure (48 / 56 / 64rem; 1376 uncapped). */
const MEASURE_PX = { prose: 768, mid: 896, wide: 1024 } as const;

const TOOL_H1 = "font-display text-4xl md:text-[56px] font-extrabold tracking-tight text-[var(--tls-fg-strong)]";

/**
 * The one page top: breadcrumb, then a plate or an h1, then one lede, then
 * whatever the page adds. It sits on .tls-container, so its left edge is the
 * library's, the nav's and every section's below it.
 */
export function PageTop({
  crumbs,
  breadcrumbLabel,
  truncateCrumb,
  title,
  plate,
  heading = "page",
  lede,
  ledeFromSm = false,
  usable = 358,
  usableDesktop,
  aside,
  asideAlign = "end",
  meta,
  children,
  measure,
  rule = true,
  tight = false,
  className,
}: PageTopProps) {
  const h1 = plate ? (
    <h1 data-ls className="m-0 text-[var(--tls-fg-strong)]" style={{ fontFamily: "var(--ls-font), var(--tls-font-display)" }}>
      <Plate as="span" text={plate} usable={usable} usableDesktop={usableDesktop ?? (measure ? MEASURE_PX[measure] : aside ? 1088 : 1376)} />
      {plate !== title && (
        <>
          {" "}
          <span className="mt-4 block max-w-[56ch] text-pretty text-[var(--tls-fg-muted)] [font:var(--tls-h4)]">
            {title}
          </span>
        </>
      )}
    </h1>
  ) : (
    <h1 className={`m-0 text-balance ${heading === "tool" ? TOOL_H1 : "tls-h1"}`}>{title}</h1>
  );

  return (
    <header className={`tls-clears-nav ${tight ? "pb-4" : "pb-12"} ${rule ? "tls-rule-b" : ""} ${className ?? ""}`}>
      <div className={`tls-container ${measure ? MEASURE[measure] : ""}`}>
        <Breadcrumb crumbs={crumbs} label={breadcrumbLabel} truncateLast={truncateCrumb} className="mb-6" />
        <div className={aside ? `grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-x-12 ${asideAlign === "end" ? "lg:items-end" : "lg:items-start"}` : undefined}>
          <div className="min-w-0">
            {h1}
            {lede && (
              <p className={`m-0 mt-4 max-w-[56ch] text-pretty text-[var(--tls-fg-muted)] [font:var(--tls-body-lg)] ${ledeFromSm ? "hidden sm:block" : ""}`}>
                {lede}
              </p>
            )}
            {meta}
          </div>
          {aside}
        </div>
        {children}
      </div>
    </header>
  );
}
